import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import FormikForm from 'src/components/common/FormikForm';
import DragAndDropFileBox from 'src/components/DragAndDropFileBox';
import FormAutoCompleteByCountry from 'src/components/Form/FormAutoCompleteByCountry';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { SingleHSCodePredictions } from 'src/model/aiHSCodeLabeling';
import AIService from 'src/services/api/aiLabeling';
import { useQueryUsageInformation } from 'src/services/queries';
import { MAX_SIZE_FILE_UPLOAD_5M } from 'src/utils/constants/config';
import { COUNTRIES, TypeCountry } from 'src/utils/constants/countries';
import { FeatureAction } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { sortObjectsArrayByKey } from 'src/utils/helpers/arrayHelpers';
import { useSingleHSCodePredictionStore } from 'src/zustand/useAILabelingStore';
import useSnackbarStore from 'src/zustand/useSnackbarStore';

const FormSingleSearch = () => {
  const { dispatch, onAIMatch, productDescription, isLoadingAIMatch, singlePredictions, country, image } =
    useSingleHSCodePredictionStore();
  const navigate = useNavigate();
  useEffect(() => {
    if (singlePredictions.status_code === SUCCESS_CODE.GET) {
      navigate(AuthRouteKeys.AIHSCodeSearchResult);
    }
  }, [singlePredictions.status_code]);

  const {
    palette: { textTertiary },
    spacing,
    zIndex,
    shape: { borderRadius },
  } = useTheme();
  const { data: dataUsage } = useQueryUsageInformation();
  const inputRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const absoluteBoxRef = useRef<HTMLDivElement>(null);
  const [paddingBottom, setPaddingBottom] = useState(40);
  const { dispatch: dispatchNoti } = useSnackbarStore();
  const { getFeaturePoints } = useFeatureAccess();

  const pointsViewAiLabeling = getFeaturePoints(URL_ENDPOINT.aiLabeling.sentenceHSCodePrediction, FeatureAction.VIEW);
  // const pointsViewAiLabeling = useGetPointsFeature(LIST_FEATURES.AI_HS_CODE_LABELING, LIST_ACTIONS.VIEW, dataUsage);

  const formik = useFormik<{ sentence?: string; country_code?: string }>({
    initialValues: {},
    onSubmit() {
      if (!country || !validateAtLeastTextOrImage()) return;
      const payload = {
        text: productDescription,
        country_code: country.code,
      };
      const promise: Promise<SingleHSCodePredictions> = AIService.getSentencePrediction(payload, image);
      onAIMatch(promise);
    },
  });

  const handleClickUpload = () => {
    inputRef.current?.click();
  };

  const validateAtLeastTextOrImage = () => {
    if (image.size == 0 && productDescription === '') return false;
    return true;
  };

  const handleChooseImage = (file: File) => {
    dispatch(file, 'image');
  };

  const handleRemoveImage = () => dispatch(new Blob(), 'image');

  const handlePaddingBottom = () => {
    if (image.size > 0) setPaddingBottom(Number(absoluteBoxRef.current?.clientHeight));
    else setPaddingBottom(40);
  };

  useEffect(() => {
    handlePaddingBottom();
  }, [image]);

  return (
    <FormikForm style={{ height: '100%' }} formik={formik}>
      {/* <Box onChange={handleChooseImage} hidden accept="image/*" ref={inputRef} component={'input'} type="file" /> */}
      <Stack justifyContent={'center'} height="100%" spacing={3}>
        <Box>
          <Typography mb={1} variant="h6">
            Instant HS Code Lookup
          </Typography>

          <Typography color={textTertiary.main} variant="body2">
            Start by entering your product description, technical specifications, and usage. Our AI will return the most
            likely results based on the analysis of global customs declarations, in priority of "most commonly declared"
            / "most commonly used".
          </Typography>
        </Box>

        <Box mt={3} />

        <FormAutoCompleteByCountry
          id={'product_country'}
          countryCodeField={'code'}
          getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
          labelField={'label'}
          required
          value={country}
          placeholder={"Product's Country"}
          countryCode={country?.code}
          onChange={(value: TypeCountry) => {
            dispatch(value, 'country');
          }}
          options={sortObjectsArrayByKey(COUNTRIES, 'label')}
        />

        <TextField
          sx={{ width: '100%' }}
          onChange={(e) => dispatch(e.target.value, 'productDescription')}
          multiline
          rows={8}
          value={productDescription}
          size="small"
          label="Please Describe Your Insights About This Product"
        />

        {image.size === 0 && (
          <DragAndDropFileBox
            type="image"
            title="Drag & Drop your image or"
            description=" Supported images: .png, .jpg, .jpeg, .gif, .webp, .svg, .tiff...'"
            limit={MAX_SIZE_FILE_UPLOAD_5M}
            accept="image/*"
            onChange={handleChooseImage}
          />
        )}

        {image.size > 0 && (
          <Box width={'fit-content'} position={'relative'}>
            <ButtonWithLoading
              onClick={handleRemoveImage}
              variant="contained"
              isRounded
              size="small"
              color="commonBlackStatesMain"
              sx={{ position: 'absolute', right: spacing(-1), top: spacing(-1) }}
            >
              <X size={12} />
            </ButtonWithLoading>
            <Box
              ref={imageRef}
              borderRadius={borderRadius / 4}
              width={80}
              height={80}
              component={'img'}
              src={URL.createObjectURL(image)}
            />
          </Box>
        )}

        <ButtonWithLoading
          isLoading={isLoadingAIMatch}
          type="submit"
          isRounded
          size="large"
          variant="contained"
          disabled={!validateAtLeastTextOrImage()}
          endIcon={<Search size={18} />}
          startIcon={<CreditCostIndicator pointData={pointsViewAiLabeling} />}
        >
          Ai Match
        </ButtonWithLoading>
      </Stack>
    </FormikForm>
  );
};
export default FormSingleSearch;
